import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetPerformanceEfficiency } from 'services/calculations/financialPerformance/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

const hasAllPerformanceEfficiencyFields = (requestData: Record<string, unknown>) => {
  return Object.values(requestData).every((value) => value !== undefined && value !== null);
};

export const usePerformanceEfficiency = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getPerformanceEfficiency = useGetPerformanceEfficiency();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [performanceEfficiencyFiled] = observedFields.performanceEfficiency.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (requestData, unit) => {
        if (!hasAllPerformanceEfficiencyFields(requestData)) return;

        const params = {
          unit,
          businessPlanId,
          requestData,
        };

        try {
          const {
            inventoryTurnOverRatio,
            receivableTurnOverRatio,
            payableTurnOverRatio,
            nonCurrentAssetTurnOverRatio,
          } = await getPerformanceEfficiency.mutateAsync(params);

          setValue(performanceEfficiencyFiled, [
            inventoryTurnOverRatio,
            receivableTurnOverRatio,
            payableTurnOverRatio,
            nonCurrentAssetTurnOverRatio,
          ]);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const performanceEfficiencyHandler = useCallback(
    async (content: IFormContentTypes['performanceEfficiency']) => {
      if (!canAccess) return;
      const [
        inventory,
        accountReceivable,
        accountPayable,
        intangibleAssets,
        propertyPlantAndEquipment,
        rightOfUseAssets,
        goodwill,
        longTermInvestments,
        deferredTaxAssets,
        otherNonCurrentAssets,
        totalCostOfSales,
        periodWiseInventory,
        totalRevenue,
        totalNonCurrentAsset,
        periodWiseAccountReceivable,
        periodWiseAccountPayable,
        operationUnit,
      ] = content;

      const performanceEfficiencyData = {
        inventory,
        accountReceivable,
        accountPayable,
        intangibleAssets,
        propertyPlantAndEquipment,
        rightOfUseAssets,
        goodwill,
        longTermInvestments,
        deferredTaxAssets,
        otherNonCurrentAssets,
        totalCostOfSales,
        periodWiseInventory,
        totalRevenue,
        totalNonCurrentAsset,
        periodWiseAccountReceivable,
        periodWiseAccountPayable,
      };

      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(performanceEfficiencyData, unit);
    },

    [debouncedUpdateFunction],
  );

  return performanceEfficiencyHandler;
};
